'use client';
import { TokenizeFileResponseBody } from 'bff';
import { IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import { useCallback, useRef, useState } from 'react';
import { Button, Textarea, cn, useToast } from 'ui';
import { getHumanFileSize } from 'utils';
import { WretchError } from 'wretch/resolver';
import { useFileUpload } from '../hooks/useFileUpload';

export interface UpdateNoteComposerData {
  message: string;
  files: { token: string }[];
}

// Inline composer for the chat layout: replaces the publish-a-message modal so
// the thread reads like a messaging app. The endpoint differs per app (insurer
// vs provider), so both the submit and the file tokenizer are injected.
export const UpdateNoteComposer = ({
  tokenizer,
  onSubmit,
  onError,
  maxFiles = 5,
  placeholder = 'Escribe tu mensaje aquí...',
  disabled,
}: {
  tokenizer: (file: {
    name: string;
    size: number;
  }) => Promise<TokenizeFileResponseBody>;
  onSubmit: (data: UpdateNoteComposerData) => Promise<unknown>;
  onError: (error: WretchError) => unknown;
  maxFiles?: number;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const upload = useFileUpload({ tokenizer, maxFiles });

  // Two error sources reach this component: API calls (wretch errors, which
  // carry a parsed `json` body and are what `onError` knows how to read) and the
  // direct upload to cloud storage, a plain Error that would crash that handler.
  const reportError = useCallback(
    (error: unknown) => {
      if (error && typeof error === 'object' && 'json' in error) {
        onError(error as WretchError);
        return;
      }

      toast({
        title: 'No se pudo completar la acción',
        description: 'Vuelve a intentarlo en unos instantes.',
      });
    },
    [onError, toast],
  );

  const remainingSlots = maxFiles - upload.files.length;
  const isBusy = isSending || isUploading || !!disabled;
  const canSend = message.trim().length > 0 && !isBusy;

  const onPickFiles = useCallback(() => fileInputRef.current?.click(), []);

  const onFilesSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(event.target.files ?? []).slice(
        0,
        remainingSlots,
      );
      // Reset first so picking the same file twice still fires onChange.
      event.target.value = '';

      if (!picked.length) return;

      setIsUploading(true);
      try {
        for (const file of picked) {
          await upload.onUploadFile(file);
        }
      } catch (err) {
        reportError(err);
      } finally {
        setIsUploading(false);
      }
    },
    [remainingSlots, reportError, upload],
  );

  const onSend = useCallback(async () => {
    if (!canSend) return;

    setIsSending(true);
    try {
      await onSubmit({
        message: message.trim(),
        files: upload.files.map(({ token }) => ({ token })),
      });
      setMessage('');
      upload.setFiles([]);
    } catch (err) {
      reportError(err);
    } finally {
      setIsSending(false);
    }
  }, [canSend, message, onSubmit, reportError, upload]);

  return (
    <div className='px-2 py-2 mt-2 bg-white'>
      {upload.files.length > 0 && (
        <div className='flex flex-row flex-wrap gap-1 mb-2'>
          {upload.files.map((file, index) => (
            <span
              key={file.token}
              className='flex items-center gap-1 rounded-md bg-stone-100 px-2 py-1 text-xs max-w-full'
            >
              <span className='truncate'>{file.name}</span>
              <span className='text-muted-foreground shrink-0'>
                ({getHumanFileSize(Number(file.size), true)})
              </span>
              <button
                type='button'
                aria-label={`Quitar ${file.name}`}
                className='shrink-0 text-muted-foreground hover:text-foreground'
                onClick={() => upload.onRemoveFile(index)}
                disabled={isBusy}
              >
                <IconX className='h-3.5 w-3.5' />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className='flex flex-row items-end gap-2'>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          className='hidden'
          onChange={onFilesSelected}
        />

        <Textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={placeholder}
          rows={1}
          disabled={isBusy}
          className={cn(
            'min-h-[2.5rem] max-h-32 rounded-xl resize-none border-gray-200 py-2',
          )}
        />

        <div className='flex flex-col items-end gap-2'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            className='shrink-0 border-gray-200'
            aria-label='Adjuntar archivos'
            onClick={onPickFiles}
            loading={isUploading}
            disabled={isBusy || remainingSlots <= 0}
          >
            <IconPaperclip className='h-4 w-4' />
          </Button>
          <Button
            type='button'
            size='sm'
            className='shrink-0'
            aria-label='Enviar mensaje'
            onClick={onSend}
            loading={isSending}
            disabled={!canSend}
          >
            <IconSend className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
};
